import React, { useState, useEffect } from "react";
import { X, Plus, Search, Building, User, Phone, Mail, Upload, AlertCircle } from "lucide-react";
import { ComponentProps, Customer } from "../types/types";
import { supabaseService } from "../SupabaseService";
import { supabase } from "../supabaseClient";

const Kunder: React.FC<ComponentProps> = ({
  cases,
  setCases,
  customers,
  setCustomers,
  openCaseForEditing,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [sortBy, setSortBy] = useState<"name" | "lastActivity">("name");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const [customerData, setCustomerData] = useState<Omit<Customer, "id">>({
    name: "",
    address: "",
    postnr: "",
    sted: "",
    orgnr: "",
    tlf: "",
    email: "",
  });

  // Reset til side 1 ved søk eller sortering
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy]);

const getFilteredCustomers = () => {
  // Hvis ingen søketekst, vis ALLE kunder (eller topp 10-20)
  if (!searchTerm || searchTerm.trim() === '') {
    return customers.slice(0, 20); // Vis første 20 kunder
  }
  
  const search = searchTerm.toLowerCase().trim();
  const searchWords = search.split(/\s+/).filter(word => word.length > 0);
  
  // Forbedret søkelogikk med fuzzy matching
  const filtered = customers.filter((customer) => {
    const customerName = customer.name || '';
    const customerNameLower = customerName.toLowerCase();
    
    // Eksakt match får høyest prioritet
    if (customerNameLower === search) return true;
    
    // Sjekk om navnet starter med søket
    if (customerNameLower.startsWith(search)) return true;
    
    // For flerdelte søk - sjekk om ALLE ord finnes
    if (searchWords.length > 1) {
      return searchWords.every(word => 
        customerNameLower.includes(word) ||
        (customer.tlf && customer.tlf.includes(word)) ||
        (customer.orgnr && customer.orgnr.includes(word))
      );
    }
    
    // Enkel substring match
    return customerNameLower.includes(search) ||
           (customer.tlf && customer.tlf.includes(search)) ||
           (customer.orgnr && customer.orgnr.includes(search));
  });
  
  // Forbedret sortering
  return filtered.sort((a, b) => {
    const aName = (a.name || '').toLowerCase();
    const bName = (b.name || '').toLowerCase();
    const searchLower = search.toLowerCase();
    
    // Eksakte treff først
    if (aName === searchLower && bName !== searchLower) return -1;
    if (bName === searchLower && aName !== searchLower) return 1;
    
    // Deretter de som starter med søket
    const aStarts = aName.startsWith(searchLower);
    const bStarts = bName.startsWith(searchLower);
    if (aStarts && !bStarts) return -1;
    if (bStarts && !aStarts) return 1;
    
    // Til slutt alfabetisk
    return aName.localeCompare(bName, 'nb-NO');
  });
};

  const filteredCustomers = getFilteredCustomers();

  // Kunde-statistikk
  const getCustomerStats = (customerId: number) => {
    const customer = customers.find((c) => c.id === customerId);
    if (!customer) return { totalSaker: 0, aktiveSaker: 0, totalOmsetning: 0, lastActivity: null };
    
    // Match på kunde navn
    const customerName = customer.name || "";
    
    const customerCases = cases.filter((case_) => 
      case_.kunde === customerName
    );

    const totalSaker = customerCases.length;
    const aktiveSaker = customerCases.filter(
      (c) => !["Arkiv", "Klar til fakturering"].includes(c.kategori)
    ).length;

    const totalOmsetning = customerCases
      .filter((c) => c.kategori === "Arkiv" || c.betalt)
      .reduce((sum, case_) => {
        return (
          sum +
          case_.varelinjer.reduce(
            (caseSum, linje) => caseSum + (parseFloat(linje.sum) || 0),
            0
          )
        );
      }, 0);

    const lastActivity =
      customerCases.length > 0
        ? new Date(
            Math.max(...customerCases.map((c) => new Date(c.opprettet || c.dato).getTime()))
          )
        : null;

    return { totalSaker, aktiveSaker, totalOmsetning, lastActivity };
  };

  // Sortering som håndterer "Etternavn, Fornavn" format
  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    if (sortBy === "name") {
      // Funksjon for å snu "Etternavn, Fornavn" til "Fornavn Etternavn"
      const formatName = (name: string) => {
        if (!name) return "";
        
        // Sjekk om navnet har komma (indikerer "Etternavn, Fornavn" format)
        if (name.includes(',')) {
          const parts = name.split(',').map(part => part.trim());
          if (parts.length === 2) {
            return `${parts[1]} ${parts[0]}`; // Snu til "Fornavn Etternavn"
          }
        }
        return name;
      };
      
      const aName = formatName(a.name || a.address || "");
      const bName = formatName(b.name || b.address || "");
      return aName.localeCompare(bName, 'nb-NO');
    } else {
      const aStats = getCustomerStats(a.id);
      const bStats = getCustomerStats(b.id);
      const aDate = aStats.lastActivity?.getTime() || 0;
      const bDate = bStats.lastActivity?.getTime() || 0;
      return bDate - aDate;
    }
  });

  // Beregn paginering
  const totalPages = Math.ceil(sortedCustomers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCustomers = sortedCustomers.slice(startIndex, endIndex);

  const resetForm = () => {
    setCustomerData({
      name: "",
      address: "",
      postnr: "",
      sted: "",
      orgnr: "",
      tlf: "",
      email: "",
    });
    setEditingCustomer(null);
    setFormError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerData.name.trim()) {
      setFormError("Navn er påkrevd");
      return;
    }

    setIsSaving(true);
    setFormError(null);
    const originalCustomers = [...customers];

    try {
      if (editingCustomer) {
        // Oppdater eksisterende kunde
        const updatedCustomer = {
          ...customerData,
          id: editingCustomer.id
        };

        // Optimistisk UI oppdatering
        setCustomers(prev =>
          prev.map(customer =>
            customer.id === editingCustomer.id
              ? updatedCustomer
              : customer
          )
        );

        // Lagre til Supabase
        const saved = await supabaseService.saveCustomer(updatedCustomer);
        if (!saved) {
          throw new Error("Kunne ikke oppdatere kunde");
        }

        console.log(`✅ Oppdatert kunde: ${updatedCustomer.name}`);
      } else {
        // Ny kunde med FIKSET ID generering
        const newCustomer: Customer = {
          id: parseInt(Date.now().toString().slice(-8)),
          ...customerData,
        };

        // Optimistisk UI oppdatering
        setCustomers(prev => [...prev, newCustomer]);

        // Lagre til Supabase
        const saved = await supabaseService.saveCustomer(newCustomer);
        if (!saved) {
          throw new Error("Kunne ikke lagre ny kunde");
        }

        console.log(`✅ Ny kunde opprettet: ${newCustomer.name}`);
      }

      resetForm();
      setShowCustomerForm(false);
    } catch (error) {
      // Rollback ved feil
      setCustomers(originalCustomers);
      setFormError(`Feil ved lagring: ${error.message}`);
      console.error("Feil ved lagring av kunde:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const editCustomer = (customer: Customer) => {
    setCustomerData({
      name: customer.name || "",
      address: customer.address || "",
      postnr: customer.postnr || "",
      sted: customer.sted || "",
      orgnr: customer.orgnr || "",
      tlf: customer.tlf || "",
      email: customer.email || "",
    });
    setEditingCustomer(customer);
    setShowCustomerForm(true);
  };

  const deleteCustomer = async (customerId: number) => {
    const customer = customers.find((c) => c.id === customerId);
    const customerName = customer?.name || "";
    
    const hasActiveCases = cases.some(
      (case_) => case_.kunde === customerName
    );

    if (hasActiveCases) {
      alert("Kan ikke slette kunde med aktive saker!");
      return;
    }

    if (!confirm("Er du sikker på at du vil slette denne kunden?")) {
      return;
    }

    setIsDeleting(customerId);
    const originalCustomers = [...customers];

    try {
      // Optimistisk UI oppdatering
      setCustomers(prev => prev.filter(c => c.id !== customerId));

      // Slett fra Supabase
      const deleted = await supabaseService.deleteCustomer(customerId);
      if (!deleted) {
        throw new Error("Kunne ikke slette kunde");
      }

      console.log(`✅ Slettet kunde: ${customerName}`);
    } catch (error) {
      // Rollback ved feil
      setCustomers(originalCustomers);
      alert(`Feil ved sletting: ${error.message}`);
      console.error("Feil ved sletting av kunde:", error);
    } finally {
      setIsDeleting(null);
    }
  };

  // CSV import med bedre duplikathåndtering og ID-generering
  const handleImportCSV = async (event: React.ChangeEvent<HTMLInputElement>) => {
  const file = event.target.files?.[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = async (e) => {
    try {
      const content = e.target?.result as string;
      const lines = content.split('\n').filter(line => line.trim());
      
      // Last eksisterende kunder
      const existingCustomers = await supabaseService.loadCustomers();
      const existingIds = new Set(existingCustomers.map(c => c.id));
      
      console.log(`📊 ${existingCustomers.length} kunder allerede i systemet`);
      
      const dataLines = lines.slice(1);
      const newCustomersToAdd: Customer[] = [];
      const customersToUpdate: Customer[] = [];
      let skippedCount = 0;

      for (const line of dataLines) {
        if (!line.trim()) continue;
        
        const parts = line.split(';').map(part => 
          part.replace(/^"|"$/g, '').trim()
        );
        
        if (parts.length < 12) continue;
        
        const csvId = parseInt(parts[0]);
        const navn = parts[2];
        
        if (!navn || navn === '""' || navn === '' || !csvId) continue;
        
        const tlf = (parts[4] || parts[5] || '').replace(/\D/g, '');
        
        // Sjekk om kunde allerede finnes
        if (existingIds.has(csvId)) {
          // Finn eksisterende kunde for mulig oppdatering
          const existingCustomer = existingCustomers.find(c => c.id === csvId);
          if (existingCustomer && !existingCustomer.tlf && tlf) {
            // Oppdater hvis telefon mangler
            customersToUpdate.push({
              ...existingCustomer,
              tlf: tlf,
              address: parts[8] || existingCustomer.address,
              email: parts[7] || existingCustomer.email
            });
          } else {
            skippedCount++;
          }
          continue;
        }
        
        // Ny kunde - bruk ID fra CSV
        const newCustomer: Customer = {
          id: csvId,
          name: navn,
          address: parts[8] || '',
          postnr: parts[10] || '',
          sted: parts[11] || '',
          orgnr: parts[3] || '',
          tlf: tlf,
          email: parts[7] || '',
        };
        
        newCustomersToAdd.push(newCustomer);
      }

      console.log(`✅ ${newCustomersToAdd.length} nye kunder`);
      console.log(`🔄 ${customersToUpdate.length} kunder kan oppdateres`);
      console.log(`⏭️ ${skippedCount} uendrede kunder hoppet over`);

      if (newCustomersToAdd.length === 0 && customersToUpdate.length === 0) {
        alert(`Ingen endringer å importere.\n\nAlle ${skippedCount} kunder finnes allerede med samme data.`);
        return;
      }

      const confirmImport = window.confirm(
        `Import-sammendrag:\n\n` +
        `✅ ${newCustomersToAdd.length} nye kunder\n` +
        `🔄 ${customersToUpdate.length} kunder å oppdatere\n` +
        `⏭️ ${skippedCount} uendret\n\n` +
        `Fortsette?`
      );

      if (!confirmImport) return;

      setFormError('Importerer...');
      let savedCount = 0;
      let updatedCount = 0;

      // Importer nye kunder i batch
      if (newCustomersToAdd.length > 0) {
        try {
          const { error } = await supabase
            .from('customers')
            .insert(newCustomersToAdd);
            
          if (error) {
            console.error('Batch import error:', error);
            // Prøv mindre batches hvis det feiler
            const batchSize = 100;
            for (let i = 0; i < newCustomersToAdd.length; i += batchSize) {
              const batch = newCustomersToAdd.slice(i, i + batchSize);
              const { error: batchError } = await supabase
                .from('customers')
                .insert(batch);
              if (!batchError) savedCount += batch.length;
            }
          } else {
            savedCount = newCustomersToAdd.length;
          }
        } catch (err) {
          console.error('Import error:', err);
        }
      }

      // Oppdater eksisterende kunder
      for (const customer of customersToUpdate) {
        const saved = await supabaseService.saveCustomer(customer);
        if (saved) updatedCount++;
      }

      setFormError(null);
      alert(
        `✅ Import fullført!\n\n` +
        `Nye kunder: ${savedCount}\n` +
        `Oppdaterte: ${updatedCount}\n\n` +
        `Totalt i systemet: ${existingCustomers.length + savedCount}`
      );

      // Last kunder på nytt
      const allCustomers = await supabaseService.loadCustomers();
      setCustomers(allCustomers);

    } catch (error) {
      console.error('Import error:', error);
      alert('❌ Feil ved import: ' + error.message);
      setFormError(null);
    }
  };
  
  reader.readAsText(file, 'ISO-8859-1');
  event.target.value = '';
};

  // Debug info
  console.log(`Søk "${searchTerm}" ga ${filteredCustomers.length} resultater`);

  return (
    <div className="bg-white rounded-lg shadow-sm border p-4">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-900">
            Kundeadministrasjon ({customers.length})
          </h3>
          <div className="flex space-x-2">
            <label className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">
              <Upload size={16} />
              <span>Importer CSV</span>
              <input
                type="file"
                accept=".csv"
                onChange={handleImportCSV}
                className="hidden"
              />
            </label>
            <button
              onClick={() => {
                resetForm();
                setShowCustomerForm(true);
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Plus size={16} />
              <span>Ny kunde</span>
            </button>
          </div>
        </div>

        {/* Search and filter */}
        <div className="flex space-x-4 mb-4">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Søk kunder (navn, telefon, org.nr, email)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
          <select
            value={sortBy}
            onChange={(e) =>
              setSortBy(e.target.value as "name" | "lastActivity")
            }
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="name">Sorter etter navn</option>
            <option value="lastActivity">Sorter etter aktivitet</option>
          </select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="text-sm text-blue-600">Totalt kunder</div>
            <div className="text-2xl font-bold text-blue-900">
              {customers.length}
            </div>
          </div>
          <div className="bg-green-50 p-3 rounded-lg">
            <div className="text-sm text-green-600">Bedriftskunder</div>
            <div className="text-2xl font-bold text-green-900">
              {customers.filter((c) => c.orgnr).length}
            </div>
          </div>
          <div className="bg-orange-50 p-3 rounded-lg">
            <div className="text-sm text-orange-600">Privatpersoner</div>
            <div className="text-2xl font-bold text-orange-900">
              {customers.filter((c) => !c.orgnr).length}
            </div>
          </div>
          <div className="bg-purple-50 p-3 rounded-lg">
            <div className="text-sm text-purple-600">Med aktive saker</div>
            <div className="text-2xl font-bold text-purple-900">
              {
                customers.filter((c) => {
                  const stats = getCustomerStats(c.id);
                  return stats.aktiveSaker > 0;
                }).length
              }
            </div>
          </div>
        </div>
      </div>

      {/* Pagination kontroller */}
      {sortedCustomers.length > itemsPerPage && (
        <div className="flex justify-between items-center mb-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">
              Viser {startIndex + 1} - {Math.min(endIndex, sortedCustomers.length)} av {sortedCustomers.length} kunder
            </span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-3 py-1 border border-gray-300 rounded text-sm"
            >
              <option value={25}>25 per side</option>
              <option value={50}>50 per side</option>
              <option value={100}>100 per side</option>
              <option value={200}>200 per side</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-200 rounded text-sm disabled:opacity-50 hover:bg-gray-300 disabled:hover:bg-gray-200"
            >
              ««
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-200 rounded text-sm disabled:opacity-50 hover:bg-gray-300 disabled:hover:bg-gray-200"
            >
              «
            </button>
            <span className="px-3 py-1 text-sm">
              Side {currentPage} av {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 bg-gray-200 rounded text-sm disabled:opacity-50 hover:bg-gray-300 disabled:hover:bg-gray-200"
            >
              »
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 bg-gray-200 rounded text-sm disabled:opacity-50 hover:bg-gray-300 disabled:hover:bg-gray-200"
            >
              »»
            </button>
          </div>
        </div>
      )}

      {/* Customer list */}
      <div className="space-y-3">
        {currentCustomers.map((customer) => {
          const stats = getCustomerStats(customer.id);
          const isCompany = !!customer.orgnr;

          return (
            <div
              key={customer.id}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    {isCompany ? (
                      <Building size={16} className="text-blue-600" />
                    ) : (
                      <User size={16} className="text-green-600" />
                    )}
                    <h4 className="font-semibold text-lg text-gray-900">
                      {customer.name || "Ukjent navn"}
                    </h4>
                    {stats.aktiveSaker > 0 && (
                      <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full">
                        {stats.aktiveSaker} aktive saker
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                    <div>
                      <div className="mb-1">
                        📍 {customer.address || "Adresse ikke oppgitt"}
                        {customer.postnr && customer.sted && (
                          <>
                            <br />
                            {customer.postnr} {customer.sted}
                          </>
                        )}
                      </div>
                      {customer.orgnr && (
                        <div className="mb-1">🏢 Org.nr: {customer.orgnr}</div>
                      )}
                    </div>
                    <div>
                      {customer.tlf && (
                        <div className="mb-1 flex items-center space-x-1">
                          <Phone size={12} />
                          <span>{customer.tlf}</span>
                        </div>
                      )}
                      {customer.email && (
                        <div className="mb-1 flex items-center space-x-1">
                          <Mail size={12} />
                          <span>{customer.email}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Customer statistics */}
                  <div className="mt-3 grid grid-cols-3 gap-4 p-3 bg-gray-50 rounded text-xs">
                    <div>
                      <span className="text-gray-500">Totalt saker:</span>
                      <div className="font-semibold">{stats.totalSaker}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Omsetning:</span>
                      <div className="font-semibold">
                        {stats.totalOmsetning.toLocaleString()} kr
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500">Siste aktivitet:</span>
                      <div className="font-semibold">
                        {stats.lastActivity
                          ? stats.lastActivity.toLocaleDateString("nb-NO")
                          : "Ingen saker"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex flex-col space-y-2 ml-4">
                  <button
                    onClick={() => editCustomer(customer)}
                    className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                  >
                    ✏️ Rediger
                  </button>
                  <button
                    onClick={() => deleteCustomer(customer.id)}
                    disabled={isDeleting === customer.id}
                    className={`px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed ${
                      isDeleting === customer.id ? "animate-pulse" : ""
                    }`}
                  >
                    {isDeleting === customer.id ? "Sletter..." : "🗑️ Slett"}
                  </button>
                  <button
                    onClick={() => {
                      console.log("Ny sak for kunde:", customer.name);
                      // TODO: Implementer ny sak for kunde
                    }}
                    className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                  >
                    ➕ Ny sak
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {currentCustomers.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>Ingen kunder funnet</p>
            {searchTerm && (
              <p className="text-sm mt-1">
                Prøv et annet søkeord eller sjekk stavemåten
              </p>
            )}
          </div>
        )}
      </div>

      {/* Customer form modal */}
      {showCustomerForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingCustomer ? "Rediger kunde" : "Ny kunde"}
                </h3>
                <button
                  onClick={() => {
                    setShowCustomerForm(false);
                    resetForm();
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {formError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                  <AlertCircle className="text-red-600" size={16} />
                  <span className="text-red-700 text-sm">{formError}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Navn *
                  </label>
                  <input
                    type="text"
                    required
                    value={customerData.name}
                    onChange={(e) =>
                      setCustomerData((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Firma/personnavn"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adresse
                  </label>
                  <input
                    type="text"
                    value={customerData.address}
                    onChange={(e) =>
                      setCustomerData((prev) => ({
                        ...prev,
                        address: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Gateadresse"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Postnr
                    </label>
                    <input
                      type="text"
                      value={customerData.postnr}
                      onChange={(e) =>
                        setCustomerData((prev) => ({
                          ...prev,
                          postnr: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="4100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sted
                    </label>
                    <input
                      type="text"
                      value={customerData.sted}
                      onChange={(e) =>
                        setCustomerData((prev) => ({
                          ...prev,
                          sted: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Jørpeland"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Org.nr (for bedrifter)
                  </label>
                  <input
                    type="text"
                    value={customerData.orgnr}
                    onChange={(e) =>
                      setCustomerData((prev) => ({
                        ...prev,
                        orgnr: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="123456789"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    value={customerData.tlf}
                    onChange={(e) =>
                      setCustomerData((prev) => ({
                        ...prev,
                        tlf: e.target.value.replace(/[^\d]/g, ""),
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="12345678"
                    maxLength={8}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    E-post
                  </label>
                  <input
                    type="email"
                    value={customerData.email}
                    onChange={(e) =>
                      setCustomerData((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="kunde@email.no"
                  />
                </div>

                <div className="flex space-x-3 pt-4 border-t">
                  <button
                    type="submit"
                    disabled={isSaving}
                    className={`flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                      isSaving ? "animate-pulse" : ""
                    }`}
                  >
                    {isSaving
                      ? "Lagrer..."
                      : editingCustomer
                      ? "Oppdater kunde"
                      : "Lagre kunde"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCustomerForm(false);
                      resetForm();
                    }}
                    disabled={isSaving}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 font-medium disabled:opacity-50"
                  >
                    Avbryt
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Kunder;